import {
  useTheme,
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  InputAdornment,
  alpha,
} from '@mui/material';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { tokens } from '../../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ProjectAddElementButton } from '../../exo/project/ProjectAddElementButton';
import { NewMetaVariableForm } from './NewMetaVariableForm';
import { updateOrCreate } from '../../special/updateOrCreate';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CodeIcon from '@mui/icons-material/Code';
import TuneIcon from '@mui/icons-material/Tune';
import WidgetsIcon from '@mui/icons-material/Widgets';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TemplateBlocks from './TemplateBlocks';

const TemplateVariables = ({
  variables = [],
  onClick,
  className = '',
  metaData,
  onMetaDataChange,
  templateType,
  onUpdate,
  templateBlocks,
  setTemplateBlocks,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const [variableMode, setVariableMode] = useState('template');
  const [searchQuery, setSearchQuery] = useState('');

  function groupByPrefix(data) {
    // 1️⃣ Group by prefix (split only by first space)
    const groups = data.reduce((acc, item) => {
      const [prefix, ...rest] = item.name.split(' ');
      const cleanName = rest.join(' ').trim();

      const newItem = { ...item, name: cleanName || item.name }; // fallback if no space

      if (!acc[prefix]) acc[prefix] = [];
      acc[prefix].push(newItem);

      return acc;
    }, {});

    // 2️⃣ Move small groups (<= 2 items) to "Others"
    const others = [];
    for (const [prefix, items] of Object.entries(groups)) {
      if (items.length <= 2) {
        others.push(...items);
        delete groups[prefix];
      }
    }
    if (others.length) groups['Others'] = others;

    // 3️⃣ Sort items alphabetically within each group
    for (const key in groups) {
      groups[key].sort((a, b) => a.name.localeCompare(b.name));
    }

    // 4️⃣ Sort group keys alphabetically, with "Others" last
    const sortedGroups = Object.keys(groups)
      .sort((a, b) => {
        if (a === 'Others') return 1;
        if (b === 'Others') return -1;
        return a.localeCompare(b);
      })
      .reduce((acc, key) => {
        acc[key] = groups[key];
        return acc;
      }, {});

    return sortedGroups;
  }

  // accordeon elements
  const [expanded, setExpanded] = useState(null);

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  // metafields allows a custopm set of variables to be used in a template
  const { metaFields } = metaData || {};
  function handleMetaDataChange(newOrUpdatedVar) {
    const newMetaFields = updateOrCreate(
      metaFields || [],
      newOrUpdatedVar,
      false,
      'key',
    );

    onMetaDataChange({ ...metaData, metaFields: newMetaFields });
  }

  function handleDeleteMetaVar(varToDelete) {
    const newMetaFields = (metaFields || []).filter(
      (item) => item.key !== varToDelete.key,
    );

    onMetaDataChange({ ...metaData, metaFields: newMetaFields });
  }

  function handleSelectedBlock(block) {
    onClick(block.key);
  }

  // Filter variables based on search query
  const filteredVariables = searchQuery
    ? variables.filter(
        (v) =>
          v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          v.value.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : variables;

  const filteredGroupedVariables = groupByPrefix(filteredVariables);
  const filteredGroupKeys = Object.keys(filteredGroupedVariables);

  return (
    <Box
      className={'h-full flex flex-col overflow-hidden ' + className}
      sx={{
        backgroundColor: colors.glass,
        backdropFilter: 'blur(12px)',
        width: '260px',
        minWidth: '260px',
        maxWidth: '300px',
        flexShrink: 0,
        borderRadius: '12px',
        border: `1px solid ${alpha(colors.card, 0.3)}`,
      }}
    >
      {/* Mode toggle - sleek segmented control */}
      <Box className="p-3">
        <ToggleButtonGroup
          size="small"
          value={variableMode}
          exclusive
          onChange={(e, val) => val && setVariableMode(val)}
          aria-label="Variable Mode"
          fullWidth
          sx={{
            backgroundColor: colors.bgInput,
            borderRadius: '10px',
            padding: '3px',
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none',
              borderRadius: '8px !important',
              mx: '2px',
              py: 0.75,
              px: 1.5,
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                backgroundColor: colors.card,
                boxShadow: `0 2px 8px ${alpha('#000', 0.15)}`,
              },
              '&:hover': {
                backgroundColor: alpha(colors.card, 0.5),
              },
            },
          }}
        >
          <Tooltip title={t('Standard template variables')} arrow>
            <ToggleButton value="template">
              <CodeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </ToggleButton>
          </Tooltip>
          {metaData && (
            <Tooltip title={t('Custom variables you define')} arrow>
              <ToggleButton value="meta">
                <TuneIcon sx={{ fontSize: 16, mr: 0.5 }} />
              </ToggleButton>
            </Tooltip>
          )}
          <Tooltip title={t('Reusable content blocks')} arrow>
            <ToggleButton value="blocks">
              <WidgetsIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>

      {/* Search field - modern floating style */}
      {variableMode === 'template' && (
        <Box className="px-3 pb-3">
          <TextField
            size="small"
            placeholder={t('Search...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, opacity: 0.4 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.bgInput,
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': {
                  border: `1px solid ${alpha(colors.card, 0.3)}`,
                },
                '&:hover fieldset': {
                  borderColor: alpha(colors.info, 0.5),
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.info,
                  borderWidth: '1px',
                },
              },
              '& .MuiInputBase-input': {
                py: 1,
                '&::placeholder': {
                  opacity: 0.5,
                },
              },
            }}
          />
        </Box>
      )}

      {/* Scrollable content area */}
      <Box
        className="flex flex-col overflow-y-auto flex-1 justify-start"
        sx={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(colors.card, 0.4),
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: alpha(colors.card, 0.6),
            },
          },
          '.MuiPaper-root': {
            height: 'auto',
          },
        }}
      >
        {variableMode === 'template' &&
          filteredGroupKeys.map((key, index) => (
            <Accordion
              key={key}
              expanded={expanded === index}
              onChange={handleChange(index)}
              disableGutters
              sx={{
                boxShadow: 'none',
                backgroundColor: 'transparent',
                '&:before': { display: 'none' },
                '& .MuiAccordionSummary-root': {
                  minHeight: '40px',
                  px: 2,
                  '&:hover': {
                    backgroundColor: alpha(colors.card, 0.2),
                  },
                },
                '& .MuiAccordionSummary-content': {
                  my: 1,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                  }}
                >
                  {key}
                </Typography>
              </AccordionSummary>
              <MuiAccordionDetails sx={{ px: 1.5, py: 0.5 }}>
                <VarList
                  varsData={filteredGroupedVariables[key]}
                  onClick={onClick}
                  colors={colors}
                />
              </MuiAccordionDetails>
            </Accordion>
          ))}

        {variableMode === 'template' && filteredGroupKeys.length === 0 && (
          <Box className="px-4 py-8 text-center">
            <SearchIcon sx={{ fontSize: 32, opacity: 0.2, mb: 1 }} />
            <Typography variant="body2" sx={{ opacity: 0.5 }}>
              {t('No variables found')}
            </Typography>
          </Box>
        )}

        {variableMode === 'meta' && (
          <MetaVarList
            varsData={metaFields}
            onChange={handleMetaDataChange}
            onClick={onClick}
            onDelete={handleDeleteMetaVar}
            colors={colors}
          />
        )}

        {variableMode === 'blocks' && (
          <TemplateBlocks
            activeType={templateType}
            onSelect={handleSelectedBlock}
            templateBlocks={templateBlocks}
            setTemplateBlocks={setTemplateBlocks}
          />
        )}
      </Box>
    </Box>
  );
};

const VarList = ({ varsData, onClick, colors }) => {
  return (
    <Box className="flex flex-col gap-0.5">
      {varsData.map((element, index) => (
        <Var
          onClick={onClick}
          key={'var' + index}
          varData={element}
          colors={colors}
        />
      ))}
    </Box>
  );
};

const MetaVarList = ({ varsData, onChange, onClick, onDelete, colors }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [varToEdit, setVarToEdit] = useState(null);

  return (
    <Box className="flex flex-col h-full gap-1 w-full p-3">
      <Collapse in={!open}>
        <ProjectAddElementButton
          label={t('Add new variable')}
          onClick={() => setOpen(true)}
          size="small"
        />
      </Collapse>
      <Collapse in={open}>
        <NewMetaVariableForm
          startData={varToEdit}
          onClose={() => {
            setVarToEdit(null);
            setOpen(false);
          }}
          onDelete={onDelete}
          onChange={onChange}
        />
      </Collapse>

      <Box className="flex flex-col gap-0.5 mt-2">
        {varsData &&
          varsData.map((element) => (
            <Var
              key={element.key}
              varData={element}
              onEdit={(d) => {
                setOpen(true);
                setVarToEdit(d);
              }}
              onClick={onClick}
              colors={colors}
            />
          ))}
      </Box>
    </Box>
  );
};

const Var = ({ varData, onClick, onEdit, colors }) => {
  return (
    <Box
      className="flex w-full overflow-hidden items-center gap-1"
      sx={{
        '.button-edit': {
          opacity: 0,
          transform: 'translateX(8px)',
          transition: 'all 0.2s ease',
        },
        '&:hover .button-edit': {
          opacity: 1,
          transform: 'translateX(0px)',
        },
      }}
    >
      <Button
        onClick={() => onClick(varData.value)}
        fullWidth
        sx={{
          textAlign: 'left',
          justifyContent: 'flex-start',
          py: 0.75,
          px: 1.5,
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 400,
          textTransform: 'none',
          color: 'inherit',
          backgroundColor: 'transparent',
          border: 'none',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: alpha(colors?.card || '#fff', 0.3),
            transform: 'translateX(4px)',
          },
        }}
      >
        {varData.name}
      </Button>
      {onEdit && (
        <IconButton
          onClick={() => onEdit(varData)}
          size="small"
          className="button-edit"
          sx={{
            p: 0.5,
            '&:hover': {
              backgroundColor: alpha(colors?.info || '#fff', 0.2),
            },
          }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};
export default TemplateVariables;
